import COLORS from '../../../utils/constants/colors'
import calendarEventModalDimensions from '../../../utils/constants/calendarEventModalDimensions'

const calendarEventClickModalStyles = (coordinates, centered) => {
  const { calendarEventModalWidth, calendarEventModalHeight } = calendarEventModalDimensions

  const adjustCoordinates = (oldCoordinates) => {
    const adjusted = { ...oldCoordinates }

    // Check for left, right edge overflow
    if (oldCoordinates.x < 0) {
      adjusted.x = 0
    } else if (oldCoordinates.x + calendarEventModalWidth > window.innerWidth) {
      adjusted.x = window.innerWidth - calendarEventModalWidth
    }

    // Check for top, bottom edge overflow
    if (oldCoordinates.y < 0) {
      adjusted.y = 0
    } else if (
      oldCoordinates.y + calendarEventModalHeight >
      window.innerHeight - calendarEventModalHeight
    ) {
      adjusted.y = window.innerHeight - 1.7 * calendarEventModalHeight
    }

    return adjusted
  }

  const adjustedCoordinates = adjustCoordinates(coordinates)

  return {
    // Motion mirrors the design source: the rail scales in from the top, the
    // details dim to 35%, and the bar fades in from just below its resting spot.
    '@keyframes dangerRailIn': {
      from: { transform: 'scaleY(0)' },
      to: { transform: 'scaleY(1)' },
    },
    '@keyframes dangerDim': {
      from: { opacity: 1 },
      to: { opacity: 0.35 },
    },
    '@keyframes dangerBarIn': {
      from: { opacity: 0, transform: 'translateY(12px)' },
      to: { opacity: 1, transform: 'none' },
    },
    // On a phone the card is wider than the screen, so it can't sit next to the clicked event.
    dialog: centered
      ? {
          margin: 16,
          width: 'calc(100% - 32px)',
          maxWidth: 'unset',
        }
      : {
          position: 'absolute!important',
          left: adjustedCoordinates.x,
          top: adjustedCoordinates.y,
          maxWidth: 'unset',
          width: calendarEventModalWidth,
        },
    // Danger state: a red rail marks the card as at risk while the details dim
    // behind the confirm bar. The event stays on screen the whole time.
    dialogDanger: {
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: 3,
        backgroundColor: COLORS.red,
        transformOrigin: 'top',
        animation: '$dangerRailIn 0.28s cubic-bezier(0.2, 0.7, 0.3, 1) both',
      },
    },
    dimmed: {
      pointerEvents: 'none',
      animation: '$dangerDim 0.24s ease both',
    },
    dangerBar: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '14px 22px',
      backgroundColor: COLORS.redTint,
      animation: '$dangerBarIn 0.24s cubic-bezier(0.2, 0.7, 0.3, 1) both',
    },
    dangerBarText: {
      flex: 1,
      fontSize: 14,
      color: COLORS.redDeep,
    },
    dangerBarActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    },
    keepButton: {
      color: COLORS.darkGrey,
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
      fontSize: 14,
      fontFamily: 'inherit',
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
      cursor: 'pointer',
    },
    deleteConfirmButton: {
      '&&': {
        backgroundColor: COLORS.red,
        color: COLORS.white,
        boxShadow: 'none',
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.2,
        padding: '10px 18px',
        minWidth: 0,
      },
      '&&:hover': {
        backgroundColor: COLORS.redDark,
      },
    },
    dialogHeader: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    headerTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      gap: '20px',
      minWidth: 0,
      flex: 1,
    },
    // On a phone a game's title ("Team A vs Team B") rarely fits one line; wrap it rather than cut it.
    headerTitle: centered
      ? {
          fontSize: '18px',
          fontWeight: '500',
          overflowWrap: 'anywhere',
          minWidth: 0,
        }
      : {
          fontSize: '22px',
          fontWeight: '500',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        },
    headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flexShrink: 0,
    },
    divider: {
      height: '1px',
      backgroundColor: COLORS.borderGrey,
      margin: '20px 0',
    },
    orangeFill: {
      '&&': {
        width: 22,
        height: 22,
        fill: COLORS.orange,
      },
    },
    deleteIconDisabled: {
      cursor: 'not-allowed!important',
    },
    flexDiv: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '20px',
      '& > svg, & > svg path': {
        fill: 'currentColor',
      },
    },
    linksLoading: {
      display: 'block',
      width: '42px',
      height: '30px',
      // The dots are drawn white for the orange buttons; dark here on the white modal.
      filter: 'invert(1)',
    },
    actionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      padding: '8px 10px',
      margin: '0 -10px 4px',
      borderRadius: '4px',
      cursor: 'pointer',
      color: 'inherit',
      textTransform: 'none',
      transition: 'background-color 0.15s ease',
      '& > svg, & > svg path': {
        fill: 'currentColor',
      },
      '&:hover': {
        backgroundColor: COLORS.backgroundGrey,
      },
    },
    actionIcon: {
      '&&': {
        fontSize: 20,
        width: 20,
        height: 20,
      },
    },
    inlineConfirm: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '10px',
      margin: '0 -10px 4px',
      borderRadius: '4px',
      backgroundColor: COLORS.backgroundGrey,
    },
    inlineConfirmText: {
      fontSize: 14,
    },
    inlineConfirmActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '12px',
    },
    inlineCancelButton: {
      '&&:hover': {
        backgroundColor: 'rgba(63, 81, 181, 0.04)',
      },
      color: COLORS.orange,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '4px',
      height: 35,
      fontSize: '0.875rem',
      fontWeight: 600,
      padding: '6px 16px',
      letterSpacing: '0.02857em',
      textTransform: 'uppercase',
      cursor: 'pointer',
    },
    notes: {
      color: COLORS.darkGrey,
      marginBottom: '20px',
    },
    copyAlert: {
      background: COLORS.backgroundGrey,
      padding: '3px 6px',
      fontSize: 12,
      fontWeight: 500,
    },
    gameSummaryAlert: {
      background: COLORS.backgroundGrey,
      padding: '3px 6px',
      fontSize: 12,
      fontWeight: 500,
    },
  }
}

export default calendarEventClickModalStyles
